import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare, Send, User, Clock, CheckCircle2,
  ChevronRight, Search, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

type Conversation = {
  conversation_id: string; company_id: string | null; user_id: string | null;
  guest_id: string | null; user_name: string; user_email: string;
  company_name: string | null; country: string | null; phone: string | null;
  page: string; last_message: string; last_message_at: string; unread_count: number;
};

type Message = {
  id: string; conversationId: string; role: string; message: string;
  userName: string; userEmail: string; adminName: string | null;
  isRead: boolean; createdAt: string;
};

export default function CPChatInbox() {
  const qc = useQueryClient();
  const [selectedConv, setSelectedConv] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [search, setSearch] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: conversations = [], isLoading } = useQuery<Conversation[]>({
    queryKey: ["cp-chat-conversations"],
    queryFn: async () => {
      const res = await fetch("/api/cp/chat/conversations", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    refetchInterval: 5000,
  });

  const { data: messages = [] } = useQuery<Message[]>({
    queryKey: ["cp-chat-messages", selectedConv],
    queryFn: async () => {
      if (!selectedConv) return [];
      const res = await fetch(`/api/cp/chat/${selectedConv}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!selectedConv,
    refetchInterval: 3000,
  });

  const sendReply = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/cp/chat/${selectedConv}/reply`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: replyText }),
      });
      if (!res.ok) throw new Error("Failed to send");
      return res.json();
    },
    onSuccess: () => {
      setReplyText("");
      qc.invalidateQueries({ queryKey: ["cp-chat-messages", selectedConv] });
      qc.invalidateQueries({ queryKey: ["cp-chat-conversations"] });
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const filtered = conversations.filter(c =>
    !search ||
    c.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.user_email?.toLowerCase().includes(search.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, c) => sum + (Number(c.unread_count) || 0), 0);

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Chat Inbox</h2>
        <p className="text-sm text-gray-500 mt-1">
          {conversations.length} conversations{totalUnread > 0 ? ` · ${totalUnread} unread` : ""}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden" style={{ height: "calc(100vh - 200px)" }}>
        <div className="flex h-full">
          <div className="w-80 border-r border-gray-200 flex flex-col shrink-0">
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search conversations..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 rounded-xl h-9 text-sm"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  No conversations yet
                </div>
              ) : (
                filtered.map(conv => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => setSelectedConv(conv.conversation_id)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      selectedConv === conv.conversation_id ? "bg-blue-50 border-l-2 border-l-blue-500" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {conv.user_name?.[0]?.toUpperCase() || <User className="w-3.5 h-3.5" />}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{conv.user_name || "Guest"}</div>
                          <div className="text-xs text-gray-400 truncate">
                            {conv.company_name
                              ? `${conv.company_name}${conv.country ? ` · ${conv.country}` : ""}`
                              : conv.user_email || "Anonymous"}
                          </div>
                        </div>
                      </div>
                      {Number(conv.unread_count) > 0 && (
                        <Badge className="bg-blue-600 text-white text-xs h-5 min-w-[20px] flex items-center justify-center rounded-full border-none">
                          {conv.unread_count}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5 line-clamp-1">{conv.last_message}</p>
                    <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      {new Date(conv.last_message_at).toLocaleString()}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col">
            {!selectedConv ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Select a conversation to view messages</p>
                </div>
              </div>
            ) : (
              <>
                <div className="px-5 py-3 border-b border-gray-200">
                  {(() => {
                    const conv = conversations.find(c => c.conversation_id === selectedConv);
                    if (!conv) return null;
                    return (
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{conv.user_name || "Guest"}</div>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                            {conv.user_email && (
                              <span className="text-xs text-gray-400">{conv.user_email}</span>
                            )}
                            {conv.company_name && (
                              <span className="text-xs text-blue-600 font-medium">{conv.company_name}</span>
                            )}
                            {conv.country && (
                              <span className="text-xs text-gray-400">{conv.country}</span>
                            )}
                            {conv.phone && (
                              <span className="text-xs text-gray-500">{conv.phone}</span>
                            )}
                            {conv.page && (
                              <span className="text-xs text-gray-400 italic">from {conv.page}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {conv.guest_id ? (
                            <Badge variant="outline" className="text-xs border-amber-300 text-amber-600 bg-amber-50">Website</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs border-blue-300 text-blue-600 bg-blue-50">App User</Badge>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.role === "admin" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                        msg.role === "admin"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-900"
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <div className={`text-xs mt-1 ${msg.role === "admin" ? "text-blue-200" : "text-gray-400"}`}>
                          {msg.role === "admin" ? msg.adminName || "Admin" : msg.userName || "User"} · {new Date(msg.createdAt).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-4 border-t border-gray-200">
                  <form
                    onSubmit={e => { e.preventDefault(); if (replyText.trim()) sendReply.mutate(); }}
                    className="flex gap-2"
                  >
                    <Input
                      placeholder="Type your reply..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      className="flex-1 rounded-xl"
                    />
                    <Button
                      type="submit"
                      disabled={!replyText.trim() || sendReply.isPending}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl gap-1.5"
                    >
                      {sendReply.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send
                    </Button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
